import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ExternalLink, Loader2, Globe } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const blank = { name: "", slug: "", country: "", h1: "", intro: "", seo_title: "", seo_description: "", published: true };

export default function AdminLanguages() {
  const [langs, setLangs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/languages").then((r) => setLangs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ name: l.name, slug: l.slug, country: l.country || "", h1: l.h1 || "", intro: l.intro || "",
      seo_title: l.seo_title || "", seo_description: l.seo_description || "", published: l.published !== false });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Language name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, slug: slugify(form.slug || form.name) };
      if (editing && editing.id) await api.put(`/admin/languages/${editing.id}`, payload);
      else await api.post("/admin/languages", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (l) => {
    if (!l.id) return;
    if (!window.confirm(`Delete the ${l.name} language page? Talent stays intact.`)) return;
    await api.delete(`/admin/languages/${l.id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Language pages</h1>
          <p className="mt-1 text-sm text-zinc-500">Create SEO landing pages per language. Voices are added automatically from the catalogue.</p>
        </div>
        <button onClick={openNew} data-testid="add-language-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover">
          <Plus size={16} /> Add language page
        </button>
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr><th className="p-3">Language</th><th className="p-3 hidden sm:table-cell">Slug</th><th className="p-3 hidden md:table-cell">Voices</th><th className="p-3">Type</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {langs.map((l) => (
              <tr key={l.slug} data-testid={`admin-language-row-${l.slug}`} className="border-b border-zinc-100 last:border-0">
                <td className="p-3 font-semibold text-zinc-800"><span className="inline-flex items-center gap-2"><Globe size={15} className="text-brand-violet" /> {l.name}</span></td>
                <td className="p-3 hidden sm:table-cell font-mono text-xs text-zinc-500">/child-voices/{l.slug}</td>
                <td className="p-3 hidden md:table-cell text-zinc-600">{l.talent_count ?? 0}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${l.managed ? "bg-violet-100 text-brand-violet" : "bg-zinc-100 text-zinc-500"}`}>{l.managed ? "Custom" : "Auto"}</span></td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${l.published !== false ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>{l.published !== false ? "Live" : "Hidden"}</span></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <a href={`/child-voices/${l.slug}`} target="_blank" rel="noreferrer" title="View" className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100"><ExternalLink size={15} /></a>
                    <button onClick={() => openEdit(l)} data-testid={`edit-language-${l.slug}`} title="Edit" className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100"><Pencil size={15} /></button>
                    {l.id && <button onClick={() => remove(l)} title="Delete" className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.name}` : "New language page"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow text-zinc-500">Language name *</Label><Input data-testid="language-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Spanish" className="rounded-xl h-10 mt-1" /></div>
              <div><Label className="label-eyebrow text-zinc-500">Country</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Spain" className="rounded-xl h-10 mt-1" /></div>
            </div>
            <div><Label className="label-eyebrow text-zinc-500">URL slug</Label><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder={slugify(form.name) || "auto-from-name"} className="rounded-xl h-10 mt-1 font-mono text-xs" /><p className="mt-1 text-xs text-zinc-400">Page will be at /child-voices/{slugify(form.slug || form.name) || "…"}</p></div>
            <div><Label className="label-eyebrow text-zinc-500">H1 heading</Label><Input value={form.h1} onChange={(e) => set("h1", e.target.value)} placeholder="Spanish child voice overs" className="rounded-xl h-10 mt-1" /></div>
            <div><Label className="label-eyebrow text-zinc-500">Intro text</Label><Textarea rows={3} value={form.intro} onChange={(e) => set("intro", e.target.value)} placeholder="Introductory paragraph for SEO and visitors…" className="rounded-xl mt-1" /></div>
            <div><Label className="label-eyebrow text-zinc-500">SEO title</Label><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className="rounded-xl h-10 mt-1" /></div>
            <div><Label className="label-eyebrow text-zinc-500">SEO meta description</Label><Textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className="rounded-xl mt-1" /></div>
            <label className="flex items-center justify-between rounded-xl border border-zinc-200 p-3"><span className="text-sm font-semibold text-zinc-700">Published (visible on site)</span><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="language-published" /></label>
            <button onClick={save} disabled={saving} data-testid="save-language" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-violet px-6 py-3 font-bold text-white hover:bg-brand-violetHover disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />} Save language page
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
